import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { ProductsContext } from '../ProductsContext';
import { useNavigate } from "react-router";
import { PRODUCT_DELTA_LIST } from '../ProductConstants';
import { TFilters } from 'types/hooks/useTable';

interface ProductCategoryDropDownProps {
    fetchData: (catId: any) => void;
    setFilters: (filters: TFilters) => void;
}

const SearchIcon = () => (
    <svg className="tw-text-ink-tertiary" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M9.5 9.5L12.5 12.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
);

const CheckIcon = () => (
    <svg className="tw-text-accent" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronIcon = () => (
    <svg className="tw-text-ink" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M5 6.5L8 4L11 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 9.5L8 12L11 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ProductCategoryDropDown: FC<ProductCategoryDropDownProps> = ({ fetchData, setFilters }) => {
    const prodContext = useContext<any>(ProductsContext);
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Auto-focus search when dropdown opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => searchInputRef.current?.focus(), 0);
        }
    }, [isOpen]);

    const handleToggle = () => {
        setIsOpen(prev => !prev);
        setSearch('');
    };

    const handleSelect = (categoryId: string) => {
        setFilters({ limit: prodContext.pageSize ?? undefined });
        prodContext.resetAttributeData();
        navigate(`${PRODUCT_DELTA_LIST}/${categoryId}`);
        setIsOpen(false);
        setSearch('');
    };

    // Filter groups by search text — keep group if any child category matches
    const filteredGroups = search.trim()
        ? (prodContext.categoryData ?? [])
            .map((group: any) => ({
                ...group,
                category: group.category.filter((cat: any) =>
                    cat.name.toLowerCase().includes(search.toLowerCase())
                )
            }))
            .filter((group: any) => group.category.length > 0)
        : (prodContext.categoryData ?? []);

    return (
        <div ref={containerRef} className="tw-relative tw-inline-block">
            {/* Trigger — bordered box matching design */}
            <button
                id="category-dropdown-trigger"
                data-testid="product-category-dropdown-trigger"
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                onClick={handleToggle}
                className={`tw-flex tw-items-center tw-gap-1.5 tw-cursor-pointer tw-transition-colors ${
                    isOpen
                        ? 'tw-bg-surface-sunken tw-border tw-border-edge-strong tw-rounded tw-px-3 tw-py-1.5 hover:tw-border-accent'
                        : 'tw-bg-transparent tw-border-0 tw-p-0 hover:tw-text-accent'
                }`}
            >
                {(prodContext.selected_Category?.name ?? '').length > 60 ? (
                    <span className="tw-max-w-[360px] tw-truncate tw-text-[20px] tw-font-semibold tw-text-ink" title={prodContext.selected_Category?.name}>
                        {prodContext.selected_Category?.name}
                    </span>
                ) : (
                    <span className="tw-whitespace-nowrap tw-text-[20px] tw-font-semibold tw-text-ink">
                        {prodContext.selected_Category?.name}
                    </span>
                )}
                <ChevronIcon />
            </button>

            {/* Popover — anchored below trigger */}
            {isOpen && (
                <div
                    id="category-dropdown-popover"
                    role="listbox"
                    className="tw-absolute tw-top-full tw-left-0 tw-mt-1.5 tw-w-[280px] tw-bg-surface tw-border tw-border-edge tw-rounded tw-shadow-lg tw-z-[1000]"
                >
                    {/* Search — bordered input, icon on right */}
                    <div className="tw-px-3 tw-py-2.5">
                        {/* Field border rests on edge-strong — the § 31 control tier, same as DS TextField/Search */}
                        <div className="tw-flex tw-items-center tw-border tw-border-edge-strong tw-rounded tw-px-2.5 tw-py-1.5 tw-gap-2">
                            <input data-testid="productlist-productcategorydropdown-input-1"
                                ref={searchInputRef}
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search"
                                className="tw-flex-1 tw-text-sm tw-text-ink tw-border-0 tw-outline-none tw-bg-transparent placeholder:tw-text-ink-tertiary"
                            />
                            <SearchIcon />
                        </div>
                    </div>

                    {/* Grouped category list */}
                    <div className="tw-overflow-y-auto tw-max-h-[300px]">
                        {filteredGroups.length === 0 ? (
                            <div className="tw-px-3 tw-py-4 tw-text-sm tw-text-ink-tertiary tw-text-center">
                                No categories found
                            </div>
                        ) : (
                            filteredGroups.map((group: any, groupIndex: number) => (
                                <div key={group.name} className={groupIndex > 0 ? 'tw-border-t tw-border-edge' : ''}>
                                    {/* Group label */}
                                    <div className="tw-px-3 tw-pt-2.5 tw-pb-1 tw-text-xs tw-font-medium tw-text-ink-tertiary tw-leading-tight">
                                        {group.name}
                                    </div>
                                    {/* Category items */}
                                    {group.category.map((cat: any) => {
                                        const isSelected = cat.id === prodContext.selected_Category?.id;
                                        return (
                                            <button data-testid="productlist-productcategorydropdown-button-2"
                                                key={cat.id}
                                                role="option"
                                                aria-selected={isSelected}
                                                type="button"
                                                onClick={() => handleSelect(cat.id)}
                                                style={{ fontWeight: isSelected ? 600 : 400 }}
                                                className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-text-sm tw-text-ink tw-text-left tw-border-0 tw-cursor-pointer tw-transition-colors tw-leading-tight ${
                                                    isSelected ? 'tw-bg-surface-sunken' : 'tw-bg-surface hover:tw-bg-surface-hover-strong'
                                                }`}
                                            >
                                                <span className="tw-truncate tw-pr-2">{cat.name}</span>
                                                {isSelected && <CheckIcon />}
                                            </button>
                                        );
                                    })}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductCategoryDropDown;
